import React, { type JSX } from 'react';
import useStyles from './styles';
import { useTheme } from '@mui/material/styles';
import { Dialog, DialogTitle, Typography, DialogActions, DialogContent, Button, Stack } from '@mui/material';

import { CustomTable } from '@fiji/web/src/components/index';
import { Header } from '@fiji/common/src/types';
import moment from 'moment';
import { CustomTransComponent } from 'components';
import { useScrollablePagination } from 'hooks';
import { useGetAllSubscriptionsQuery } from '@fiji/common/src/features/Subscriptions/subscriptionApi';
import { useGetAllSubscriptionsV2Query } from '@fiji/common/src/features/Subscriptions/subscriptionV2Api';
import { useLocation, useParams } from 'react-router-dom';
import { useEntitlementsV2Flag } from '@fiji/common/src/hooks';

/**
 * The DiscardModalProps type is used in a TypeScript React component and includes properties for
 * visibility and a modal handler function.
 * @property {boolean} isVisible - The `isVisible` property is a boolean value that determines whether
 * the discard modal is visible or not. If `isVisible` is `true`, the modal is visible. If `isVisible`
 * is `false`, the modal is hidden.
 * @property modalHandler - The `modalHandler` property is a function that takes a boolean parameter
 * `action` and does something with it. It is used to handle the action performed on the modal, such as
 * closing it or confirming a discard action.
 */
type SubscriptionPlanModalProps = {
    isVisible: boolean;
    modalHandler: (action: boolean) => void;
    okHandler: (e: any) => Promise<void>;
    subscriptionPlan: any;
    total: number;
    handleSubscriptionSelect?: (arg0: string, arg1?: string) => void;
    handlepagePayload?: (arg0: any) => void;
    isLoading: boolean;
    deviceCountConfig?: any;
    deviceModel: any;
};

/**
 * The `DiscardModal` function is a TypeScript React component that renders a dialog box with a title,
 * message, and two buttons for canceling or discarding changes.
 * @param {SubscriptionPlanModalProps}  - - `isVisible`: a boolean value indicating whether the modal is visible
 * or not.
 * @returns The code is returning a JSX element, specifically a `Dialog` component with various child
 * components such as `DialogTitle`, `DialogContent`, and `DialogActions`.
 */
export const SubscriptionPlanModal = ({
    isVisible,
    modalHandler,
    // connectionString,
    // subscriptionPlan,
    okHandler,
    total,
    handleSubscriptionSelect,
    handlepagePayload,
    // isLoading,
    // deviceCountConfig,
    deviceModel,
}: SubscriptionPlanModalProps): JSX.Element => {
    const theme: any = useTheme();
    const classes = useStyles(theme);
    const { realmPrefix } = useParams();
    const { state } = useLocation();
    const { isEntitlementsV2, isFlagLoading } = useEntitlementsV2Flag();

    const tableRef = React.useRef<any>(undefined);
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

    const pageSize = 10;
    const [page, setPage] = React.useState(0);
    const [sort, setSort] = React.useState<any>(undefined);

    const {
        data: allSubscriptionsV1,
        isLoading: isLoadingV1,
        isFetching: isFetchingV1,
    } = useGetAllSubscriptionsQuery(
        {
            body: {
                page,
                size: pageSize,
                ...(sort && { sort }),
                filters: {
                    profileId: deviceModel?.profileId,
                },
            },
            headers: {
                'x-realm': realmPrefix,
                ...(state?.orgId && {
                    organizationId: state?.orgId,
                }),
            },
            params: {
                status: 'ACTIVE',
            },
        },
        { refetchOnMountOrArgChange: true, skip: !isVisible || isFlagLoading || isEntitlementsV2 }
    );

    const {
        data: allSubscriptionsV2,
        isLoading: isLoadingV2,
        isFetching: isFetchingV2,
    } = useGetAllSubscriptionsV2Query(
        {
            body: {
                page,
                size: pageSize,
                ...(sort && { sort }),
                filters: {
                    profileId: deviceModel?.profileId,
                    status: ['active', 'deferred'],
                },
            },
            headers: {
                'x-realm': realmPrefix,
                ...(state?.orgId && {
                    organizationId: state?.orgId,
                }),
            },
        },
        { refetchOnMountOrArgChange: true, skip: !isVisible || isFlagLoading || !isEntitlementsV2 }
    );

    const allSubscriptions = isEntitlementsV2 ? allSubscriptionsV2 : allSubscriptionsV1;
    const isLoading = isEntitlementsV2 ? isLoadingV2 : isLoadingV1;
    const isFetching = isEntitlementsV2 ? isFetchingV2 : isFetchingV1;

    const { reset } = useScrollablePagination({
        containerRef: scrollContainerRef,
        pageSize,
        page,
        setPage,
        total: allSubscriptions?.data?.total ?? allSubscriptions?.data?.totalRecords,
        lastPageCount: allSubscriptions?.data?.records?.length,
        isLoading,
        isFetching,
        resetDeps: [sort],
        enabled: isVisible,
    });

    const [listRecords, setListRecords] = React.useState<any[]>([]);
    const mergeUniqueById = React.useCallback((prev: any[], next: any[]): any[] => {
        const merged = [...prev, ...next];
        return merged.reduce((acc: any[], current: any) => {
            if (!acc.find((item) => item?.id === current?.id)) acc.push(current);
            return acc;
        }, []);
    }, []);

    React.useEffect(() => {
        const newRecords = allSubscriptions?.data?.records ?? [];
        if (page === 0) {
            if (!isFetching) {
                setListRecords(newRecords);
            }
        } else if (newRecords.length) {
            setListRecords((prev) => mergeUniqueById(prev, newRecords));
        }
    }, [allSubscriptions?.data?.records, page, isFetching, mergeUniqueById]);
    const [selectedId, setSelectedId] = React.useState<string>('');
    const handleRadioSelect = (data: string): void => {
        setSelectedId(data);
        const selectedRecord = listRecords.find((r: any) => r.id === data.toString());
        if (handleSubscriptionSelect) handleSubscriptionSelect(data.toString(), selectedRecord?.deviceLicenseId);
    };
    const handleFilter = (filteredValues: any, sortedData: any): any => {
        if (Object.keys(sortedData)?.length) {
            setSort(sortedData);
            if (handlepagePayload && !allSubscriptions) {
                handlepagePayload((prev: any) => ({ ...prev, sort: sortedData }));
            }
        }
    };
    const getSubscriptionCell = (cell: any): JSX.Element => (
        <Typography variant="body2" fontSize={'14px'}>
            {(isEntitlementsV2 ? cell?.featurePackageName : cell?.name) ?? '-'}
        </Typography>
    );
    const getStartDate = (data: any): string => moment(data?.startDate).format('LL') ?? '--';
    const getDeviceCount = (data: any): string => {
        if (isEntitlementsV2) {
            if (data?.deviceLimit === null) return 'N/A';
            if (data?.deviceLimit === 0) return '0';
            return `${data?.deviceCount}/${data?.deviceLimit}`;
        }
        if (data?.deviceLimit === false) {
            return 'N/A';
        }
        if (data?.totalQuantity === 0) {
            return '0';
        }
        if (data?.totalQuantity !== 0) {
            return `${data?.fulfilledQuantity}/${data?.totalQuantity}`;
        }
        return 'N/A';
    };
    const getEndDate = (data: any): string =>
        data?.endDate === null ? 'N/A' : moment(data?.endDate).format('LL') ?? '--';

    const prepareHeaderColumns = (): Header[] => {
        const columns: Header[] = [
            {
                header: (isEntitlementsV2 ? (
                    <CustomTransComponent translationKey={'COMMON:SUBSCRIPTION'} />
                ) : (
                    <CustomTransComponent translationKey={'COMMON:NAME'} />
                )) as any,
                cell: getSubscriptionCell,
                accessor: isEntitlementsV2 ? 'featurePackageName' : 'name',
                isSortable: true,
            },
            {
                header: (<CustomTransComponent translationKey={'ENTITLEMENTS:SUBSCRIPTION.START_DATE'} />) as any,
                cell: getStartDate,
                accessor: 'startDate',
            },
            {
                header: (<CustomTransComponent translationKey={'ENTITLEMENTS:SUBSCRIPTION.END_DATE'} />) as any,
                cell: getEndDate,
                accessor: 'endDate',
            },
            {
                header: (<CustomTransComponent translationKey={'ENTITLEMENTS:SUBSCRIPTION.DEVICE_COUNT'} />) as any,
                cell: getDeviceCount,
                accessor: 'deviceCount',
            },
            {
                header: '',
                isRadio: true,
                width: '5%',
            },
        ];

        return columns;
    };

    const handleBackAction = (): void => {
        modalHandler(false);
        reset();
    };

    return (
        <Dialog
            open={isVisible}
            onClose={(): void => {
                modalHandler(false);
                reset();
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            data-testid="SubscriptionPlanDialog"
        >
            <DialogTitle id="alert-dialog-title" data-testid="SubscriptionPlanHeadingPopUp">
                <Typography variant={'h6'}>
                    {' '}
                    {<CustomTransComponent translationKey={'ADD_DEVICE_FORM:SUBSCRIPTION_TITLE'} />}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Stack mb={2}>
                    <Typography variant="body2" style={{ marginBottom: 10 }}>
                        {<CustomTransComponent translationKey={'ADD_DEVICE_FORM:SUBSCRIPTION_TITLE_MSG'} />}
                    </Typography>

                    <Stack my={2} className="max-height-225 overflow-y-auto" ref={scrollContainerRef}>
                        <CustomTable
                            key="e13aef249f024g"
                            data={listRecords ?? []}
                            ref={tableRef}
                            isLoading={isLoading}
                            keyToTraverse="id"
                            handleFilterChange={handleFilter}
                            total={total}
                            handleRadioSelect={handleRadioSelect}
                            headers={prepareHeaderColumns()}
                            noDataFoundTitle={'No Subscription Plans Found'}
                            disableRow={(data: any): boolean => {
                                if (data?.skipDeviceCount) return false;
                                return isEntitlementsV2
                                    ? data?.deviceLimit !== null && data?.deviceLimit - data?.deviceCount <= 0
                                    : data?.remainingQuantity === 0;
                            }}
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions className={classes.MuiDialogActionsRoot}>
                <Button
                    autoFocus
                    color="secondary"
                    variant="outlined"
                    onClick={handleBackAction}
                    data-testid="copy-action"
                >
                    <CustomTransComponent translationKey={'COMMON:BACK'} />
                </Button>

                <Button
                    autoFocus
                    disabled={Boolean(!selectedId?.length)}
                    color="secondary"
                    sx={{
                        backgroundColor: theme?.palette?.primary?.main,
                        '&:hover': {
                            backgroundColor: theme?.palette?.primary?.main,
                        },
                    }}
                    variant="contained"
                    onClick={okHandler}
                    data-testid="SelectSubscriptionPlan"
                >
                    <CustomTransComponent translationKey={'ADD_DEVICE_FORM:SAVE_DEVICE'} />
                </Button>
            </DialogActions>
        </Dialog>
    );
};
